import { fromJS } from 'immutable';
import * as types from 'constants/ActionTypes';
import transformAncillariesToSelectedProducts, {
  enrichSelectedProductsWithCatalog,
} from 'utils/productsCart/transformAncillariesToSelectedProducts';
import {
  removeProductFromAllSegments,
  setProductStockToZeroInAllSegments,
} from 'utils/productsCart/handleUnavailableProducts';

/**
 * Default initial state for products cart
 * catalog structure example: {
 *   outgoing: [
 *     { code: 'P1', name: 'Product 1', price: 100 },
 *     { code: 'P2', name: 'Product 2', price: 200 }
 *   ],
 *   incoming: [
 *     { code: 'BEB001', name: 'Coca Cola 355ml', description: 'Refreshing soft drink', price: 45, stock: 120, image: 'https://example.com/cocacola.jpg', imageFormat: 'jpg' },
 *     { code: 'SNK002', name: 'Papas Fritas', description: 'Crunchy potato chips', price: 35, stock: 85, image: 'https://example.com/chips.jpg', imageFormat: 'jpg' }
 *   ]
 * }
 * selectedProducts structure example:
 * {
 *   0: {
 *     outgoing: [
 *       { code: 'P1', name: 'Product 1', price: 100, quantity: 2, stock: 50, image: '...', description: '...' },
 *       { code: 'P2', name: 'Product 2', price: 200, quantity: 1, stock: 30, image: '...', description: '...' }
 *     ],
 *     incoming: [
 *       { code: 'BEB001', name: 'Coca Cola 355ml', price: 45, quantity: 1, stock: 120, image: '...', description: '...' },
 *       { code: 'SNK002', name: 'Papas Fritas', price: 35, quantity: 2, stock: 85, image: '...', description: '...' }
 *     ]
 *   },
 *   1: {
 *     outgoing: [
 *       { code: 'P1', name: 'Product 1', price: 100, quantity: 1, stock: 50, image: '...', description: '...' }
 *     ],
 *     incoming: [
 *       { code: 'BEB001', name: 'Coca Cola 355ml', price: 45, quantity: 1, stock: 120, image: '...', description: '...' },
 *       { code: 'SNK002', name: 'Papas Fritas', price: 35, quantity: 2, stock: 85, image: '...', description: '...' }
 *     ]
 *   }
 * }
 * Note: Complete product information (name, price, image, etc.) is stored with each selection
 * to avoid needing to join with catalog during rendering. Stock validation is calculated
 * dynamically in components considering all passengers' selections.
 * When a passenger is deleted, their key is completely removed from the object.
 */
const defaultState = fromJS({
  catalog: {
    outgoing: [],
    incoming: [],
  },
  selectedProducts: {}, // Object keyed by passengerIndex, each containing array of products
  isLoading: false,
  isEnabled: true,
  error: null,
});

/**
 * Products Cart reducer
 * Manages the state of product catalog and shopping cart
 * Structure: selectedProducts = { passengerIndex: [products] }
 *
 * @param {Map} state - Current state (Immutable.Map)
 * @param {Object} action - Redux action
 * @returns {Map} Updated state
 */
export default function productsCart(state = defaultState, action) {
  switch (action.type) {
    // ===== CATALOG ACTIONS =====
    case types.REQUEST_PRODUCT_CATALOG:
      return state.set('isLoading', true).set('error', null);

    case types.RECEIVE_PRODUCT_CATALOG: {
      const newCatalog = fromJS({
        outgoing: action.products.outgoing,
        incoming: action.products.incoming,
      });

      // Enrich existing selectedProducts with catalog information if they exist
      const currentSelectedProducts = state.get('selectedProducts');
      let enrichedSelectedProducts = currentSelectedProducts;

      if (currentSelectedProducts && currentSelectedProducts.size > 0) {
        enrichedSelectedProducts = enrichSelectedProductsWithCatalog({
          selectedProducts: currentSelectedProducts,
          catalog: newCatalog,
        });
      }

      return state
        .set('catalog', newCatalog)
        .set('selectedProducts', enrichedSelectedProducts)
        .set('isLoading', false)
        .set('isEnabled', true)
        .set('error', null);
    }

    case types.RECEIVE_PRODUCT_CATALOG_ERROR:
      return state.set('isLoading', false).set('error', action.error);

    // ===== CART ITEM ACTIONS =====
    case types.ADD_PRODUCT_TO_CART: {
      const { product, quantity, passengerIndex, segment } = action;
      let selectedProducts = state.get('selectedProducts');

      // Use getIn to get the current passenger data, if not exists create new Map
      let passengerProducts = selectedProducts.get(String(passengerIndex));

      if (!passengerProducts) {
        passengerProducts = fromJS({
          outgoing: [],
          incoming: [],
        });
      }

      // Get the segment array
      let segmentProducts = passengerProducts.get(segment) || fromJS([]);

      // Check if product already exists for this passenger in the specified segment
      const existingProductIndex = segmentProducts.findIndex(
        (item) => item.get('code') === product.code,
      );

      if (existingProductIndex >= 0) {
        // Update existing product quantity
        segmentProducts = segmentProducts.update(existingProductIndex, (item) => {
          return item.update('quantity', (q) => q + quantity);
        });
      } else {
        // Add new product with complete information and button state
        segmentProducts = segmentProducts.push(
          fromJS({
            code: product.code,
            name: product.name,
            price: product.price,
            stock: product.stock,
            image: product.image,
            imageFormat: product.imageFormat,
            description: product.description,
            quantity,
          }),
        );
      }

      // Update passenger products with new segment data
      passengerProducts = passengerProducts.set(segment, segmentProducts);

      // Update selected products with new passenger data
      selectedProducts = selectedProducts.set(String(passengerIndex), passengerProducts);

      return state.set('selectedProducts', selectedProducts);
    }

    case types.REMOVE_PRODUCT_FROM_CART: {
      const { productCode, passengerIndex, segment } = action;
      let selectedProducts = state.get('selectedProducts');
      const passengerProducts = selectedProducts.get(String(passengerIndex));

      if (passengerProducts) {
        const segmentProducts = passengerProducts.get(segment);

        if (segmentProducts) {
          // Filter out the product
          const updatedSegmentProducts = segmentProducts.filter(
            (item) => item.get('code') !== productCode,
          );

          // Update passenger products with new segment data
          const updatedPassengerProducts = passengerProducts.set(segment, updatedSegmentProducts);

          // Update selected products
          selectedProducts = selectedProducts.set(String(passengerIndex), updatedPassengerProducts);
        }
      }

      return state.set('selectedProducts', selectedProducts);
    }

    case types.UPDATE_PRODUCT_QUANTITY: {
      const { productCode, quantity, passengerIndex, segment } = action;
      let selectedProducts = state.get('selectedProducts');
      const passengerProducts = selectedProducts.get(String(passengerIndex));

      if (passengerProducts) {
        const segmentProducts = passengerProducts.get(segment);

        if (segmentProducts) {
          let updatedSegmentProducts;

          if (quantity === 0) {
            // Remove product if quantity is 0
            updatedSegmentProducts = segmentProducts.filter(
              (item) => item.get('code') !== productCode,
            );
          } else {
            // Update quantity
            updatedSegmentProducts = segmentProducts.map((item) => {
              if (item.get('code') === productCode) {
                return item.set('quantity', quantity);
              }
              return item;
            });
          }

          const updatedPassengerProducts = passengerProducts.set(segment, updatedSegmentProducts);
          selectedProducts = selectedProducts.set(String(passengerIndex), updatedPassengerProducts);
        }
      }

      return state.set('selectedProducts', selectedProducts);
    }

    case types.HANDLE_PRODUCT_UNAVAILABLE: {
      const { productCodes } = action;
      if (!productCodes || productCodes.length === 0) {
        return state;
      }

      let selectedProducts = state.get('selectedProducts');
      let catalog = state.get('catalog');

      // Process each unavailable product code
      productCodes.forEach((productCode) => {
        // Remove product from all passengers and segments
        selectedProducts = selectedProducts.map((passengerProducts) => {
          if (!passengerProducts) {
            return passengerProducts;
          }
          return removeProductFromAllSegments(passengerProducts, productCode);
        });

        // Update catalog: set stock to 0 for the product in both segments
        catalog = setProductStockToZeroInAllSegments(catalog, productCode);
      });

      return state.set('selectedProducts', selectedProducts).set('catalog', catalog);
    }

    // ===== SYNC PRODUCTS FROM PURCHASE =====
    case types.RECEIVE_PURCHASE: {
      // Extract products from purchase passengers if they exist
      // This happens when loading a purchase that already has products saved
      if (action.purchase?.passengers && action.purchase.passengers.length > 0) {
        const passengers = fromJS(action.purchase.passengers);
        const catalog = state.get('catalog');

        // Transform ancillaries from passengers to selectedProducts format
        const selectedProducts = transformAncillariesToSelectedProducts({
          passengers,
          catalog,
        });

        // Only update selectedProducts if there are products to sync
        // Don't overwrite if selectedProducts already exists (user might be editing)
        const currentSelectedProducts = state.get('selectedProducts');
        if (
          selectedProducts.size > 0 &&
          (!currentSelectedProducts || currentSelectedProducts.size === 0)
        ) {
          return state.set('selectedProducts', selectedProducts);
        }
      }

      return state;
    }

    case types.RESET_PURCHASE:
      return state
        .set('catalog', fromJS({ outgoing: [], incoming: [] }))
        .set('selectedProducts', fromJS({}))
        .set('error', null)
        .set('isLoading', false)
        .set('isEnabled', true);

    default:
      return state;
  }
}
